import type { CSSProperties } from "react";

interface ProgressBarProps {
  label: string;
  value: number;
  target: number;
  unit: string;
  tone?: "primary" | "protein" | "neutral" | "warning";
  compact?: boolean;
}

export function ProgressBar({
  label,
  value,
  target,
  unit,
  tone = "primary",
  compact = false,
}: ProgressBarProps) {
  const percentage = target > 0 ? Math.max(0, Math.min(100, (value / target) * 100)) : 0;
  const formattedValue = formatAmount(value);
  const formattedTarget = formatAmount(target);

  return (
    <div className={`progress ${compact ? "progress--compact" : ""}`}>
      <div className="progress__labels">
        <span>{label}</span>
        <span className="progress__amount">
          {formattedValue} / {formattedTarget} {unit}
        </span>
      </div>
      <div
        className="progress__track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={target}
        aria-valuenow={Math.min(value, target)}
        aria-valuetext={`${formattedValue} of ${formattedTarget} ${unit}`}
      >
        <span
          className={`progress__fill progress__fill--${tone}`}
          style={{ "--progress": `${percentage}%` } as CSSProperties}
        />
      </div>
    </div>
  );
}

function formatAmount(value: number) {
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
}
